import { z } from "zod";
import { DEFAULT_PRIMARY_COLOR, DEFAULT_REMINDER_CADENCE } from "./types.js";

/** Un nombre de jours : entier, positif, et pas au-delà de l'année. */
const days = (max: number) =>
  z
    .number({ message: "Un nombre de jours est attendu" })
    .int("Un nombre entier de jours est attendu")
    .min(0, "Le délai ne peut pas être négatif")
    .max(max, `Le délai ne peut pas dépasser ${max} jours`);

/**
 * La cadence, validée.
 *
 * L'ordre strict n'est pas une coquetterie : un palier « ferme » placé avant
 * le palier « amical » rendrait le second inatteignable, et le patron
 * découvrirait le trou le jour où un client s'étonne d'avoir reçu une mise en
 * demeure sans rappel préalable.
 */
export const reminderCadenceInput = z
  .object({
    preventiveDaysBefore: days(90).default(DEFAULT_REMINDER_CADENCE.preventiveDaysBefore),
    friendlyDaysAfter: days(365).default(DEFAULT_REMINDER_CADENCE.friendlyDaysAfter),
    firmDaysAfter: days(365).default(DEFAULT_REMINDER_CADENCE.firmDaysAfter),
    formalDaysAfter: days(365).default(DEFAULT_REMINDER_CADENCE.formalDaysAfter),
  })
  .superRefine((c, ctx) => {
    if (c.firmDaysAfter <= c.friendlyDaysAfter) {
      ctx.addIssue({
        code: "custom",
        path: ["firmDaysAfter"],
        message: "La relance ferme doit venir après la relance amicale",
      });
    }
    if (c.formalDaysAfter <= c.firmDaysAfter) {
      ctx.addIssue({
        code: "custom",
        path: ["formalDaysAfter"],
        message: "La mise en demeure doit venir après la relance ferme",
      });
    }
  });

export type ReminderCadenceInput = z.infer<typeof reminderCadenceInput>;

export const readTenantConfigInput = z.object({
  confirmationId: z.string().optional(),
});

export type ReadTenantConfigInput = z.infer<typeof readTenantConfigInput>;

export const saveReminderCadenceInput = z.object({
  confirmationId: z.string().optional(),
  cadence: reminderCadenceInput,
});

export type SaveReminderCadenceInput = z.infer<typeof saveReminderCadenceInput>;
export type SaveReminderCadenceFormInput = z.input<typeof saveReminderCadenceInput>;

/**
 * L'identité visuelle, validée.
 *
 * L'URL du logo est restreinte à `https://`. Ce n'est pas de la méfiance
 * envers le patron qui la saisit — c'est que cette valeur ressort par
 * `branding_for_host`, qui la sert **sans authentification** au navigateur
 * d'un visiteur. Un `javascript:` y serait inerte dans un `<img>`, mais rien
 * ne garantit qu'elle n'atterrira jamais ailleurs qu'un `<img>`. Fermer au
 * schéma près coûte une ligne.
 */
export const brandingInput = z.object({
  logoUrl: z
    .string()
    .trim()
    .max(2000, "Adresse du logo trop longue")
    .url("Adresse du logo invalide")
    .refine((v) => v.startsWith("https://"), "L'adresse du logo doit être en https")
    .nullish()
    .or(z.literal("").transform(() => null))
    // `null` et jamais `undefined` : « pas de logo » est un état, pas un
    // champ omis. Un `undefined` qui traverserait jusqu'à l'écriture
    // laisserait la fonction SQL retomber sur l'ancienne valeur, et retirer
    // un logo n'aurait aucun effet.
    .transform((v) => v ?? null),

  primaryColor: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .refine(
      (v) => /^#[0-9A-F]{6}$/.test(v),
      "Couleur attendue au format #RRGGBB",
    )
    .default(DEFAULT_PRIMARY_COLOR),
});

export type BrandingInput = z.infer<typeof brandingInput>;

export const saveBrandingInput = z.object({
  confirmationId: z.string().optional(),
  branding: brandingInput,
});

export type SaveBrandingInput = z.infer<typeof saveBrandingInput>;
export type SaveBrandingFormInput = z.input<typeof saveBrandingInput>;

/**
 * L'objectif de marge, validé.
 *
 * Une chaîne vide vaut « pas d'objectif » et non zéro : le champ du formulaire
 * qu'on vide est le seul geste par lequel on retire un objectif, et le laisser
 * tomber sur `0` poserait silencieusement un objectif de 0 % — atteint par
 * construction, donc une jauge verte permanente qui ne dit plus rien.
 *
 * Borné à 100 : au-delà, la marge n'est plus une marge. Borné en bas à 0 plutôt
 * qu'ouvert au négatif — se fixer pour objectif de perdre de l'argent n'est pas
 * un cas d'usage, c'est une faute de frappe.
 */
export const marginTargetInput = z
  .union([z.literal(""), z.null(), z.undefined(), z.coerce.number()])
  .transform((v) => (v === "" || v == null ? null : v))
  .refine(
    (v) => v == null || (Number.isFinite(v) && v >= 0 && v <= 100),
    "L’objectif de marge se situe entre 0 et 100 %",
  )
  // Un dixième de point suffit à un objectif d'entreprise, et arrondir ici
  // évite qu'un « 22,333333 » collé depuis un tableur ne se compare à une
  // marge affichée au dixième.
  .transform((v) => (v == null ? null : Math.round(v * 10) / 10));

export const saveMarginTargetInput = z.object({
  confirmationId: z.string().optional(),
  targetPercent: marginTargetInput,
});

export type SaveMarginTargetInput = z.infer<typeof saveMarginTargetInput>;
export type SaveMarginTargetFormInput = z.input<typeof saveMarginTargetInput>;

const tauxMajoration = z.coerce
  .number({ invalid_type_error: "Un taux s’écrit en chiffres" })
  .min(0, "Un taux ne peut pas être négatif")
  .max(200, "Un taux de majoration ne dépasse pas 200 %")
  .transform((v) => Math.round(v * 10) / 10);

export const saveOvertimeRatesInput = z.object({
  confirmationId: z.string().optional(),
  complementaryRate: tauxMajoration,
  firstRate: tauxMajoration,
  secondRate: tauxMajoration,
});

export type SaveOvertimeRatesInput = z.infer<typeof saveOvertimeRatesInput>;

export const saveSafetySettingsInput = z.object({
  confirmationId: z.string().optional(),
  talkFrequencyDays: z.coerce
    .number({ message: "Une fréquence en jours est attendue" })
    .int("Une fréquence se compte en jours entiers")
    .min(1, "Au moins un jour")
    .max(90, "Au plus quatre-vingt-dix jours"),
});

export type SaveSafetySettingsInput = z.infer<typeof saveSafetySettingsInput>;

export const saveSubcontractingSettingsInput = z.object({
  confirmationId: z.string().optional(),
  applyVigilanceThreshold: z.boolean(),
  vigilanceBasis: z.enum(["cumulative", "per_contract"]).default("cumulative"),
});

export type SaveSubcontractingSettingsInput = z.infer<typeof saveSubcontractingSettingsInput>;
export type SaveSubcontractingSettingsFormInput = z.input<typeof saveSubcontractingSettingsInput>;
